/**
 * NOS-263: Org-loading guard triad for dashboard pages — spinner while orgs
 * load, error + Retry when the fetch failed, onboarding link when the user
 * has no orgs. Renders children only once an org exists. Replaces the
 * per-page copies (which had drifted: ContactDetailPage lost the error
 * branch and showed "No organization found" on fetch failure — audit B8).
 */
import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useOrgStore } from "@/stores/orgStore"

export function RequireOrg({ children }: { children: ReactNode }) {
  const { userOrgs, isLoading, fetchError, fetchUserOrgs } = useOrgStore()

  if (isLoading && userOrgs.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
        <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
        <p className="text-muted-foreground">Loading your organization...</p>
      </div>
    )
  }

  if (fetchError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4">
        <p className="text-center text-destructive">{fetchError}</p>
        <Button onClick={fetchUserOrgs}>Retry</Button>
      </div>
    )
  }

  if (userOrgs.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4">
        <p className="text-center text-muted-foreground">No organization found.</p>
        <Button asChild>
          <Link to="/onboarding">Create organization</Link>
        </Button>
      </div>
    )
  }

  return <>{children}</>
}
